/**
 * Competence Map Screen (route `CompetenceMap`)
 *
 * Grammar's face. The 62 competences ADR-034 tracks on every analysed message
 * had no learner-facing surface at all: mastery was written continuously and
 * read only by the tutor's own prompt. This is the Sound Map's sibling for
 * grammar — header summary, a due-for-review section, CEFR bands of chips, and
 * a detail sheet whose primary action opens a session focused on that
 * competence.
 *
 * Two renderings carry design decisions rather than taste:
 *
 *  - **Bands above the learner's level start collapsed.** The server splits
 *    "no row" into `untried` (not seen yet) and `upcoming` (comes later) —
 *    CMAP-001 — and this is where that split becomes visible. On day one every
 *    row is empty; rendered flat that reads as "you are bad at everything",
 *    where the split says 19 blanks worth filling and 42 things not yet due.
 *  - **Progress excludes `upcoming`.** A ring must be completable; the same
 *    argument ADR-036 makes for the *attainable* vocabulary core.
 *
 * States render straight from the server's `state` field — no client-side
 * threshold logic (see docs/specs/api/competence-map.api.md).
 *
 * @see docs/specs/mobile/competence-map.mobile.md
 */

import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { ErrorState, LoadingState } from '../components/ui';
import Ionicons from '../components/ui/Ionicons';
import { AIConsentModal } from '../components/AIConsentModal';
import { FS_XS, FS_SM, FS_MD } from '../theme';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useCompetenceFocusSession } from '../hooks/useCompetenceFocusSession';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useCompetenceMapStore,
  selectCompetenceMapData,
  selectCompetenceMapLoading,
  selectCompetenceMapErrorInfo,
  selectLoadCompetenceMap,
  selectClearCompetenceMapError,
} from '../store/competenceMapStore';
import { CompetenceChip } from '../components/competencemap/CompetenceChip';
import { CompetenceDetailSheet } from '../components/competencemap/CompetenceDetailSheet';
import {
  attainableTotal,
  formatSummaryLine,
  translateBandTitle,
  CEFR_BANDS,
  type CefrBand,
} from '../components/competencemap/competenceMapText';
import type { CompetenceMapItem } from '../generated/api/http-contracts';

/** Rank of a CEFR level, for "is this band above the learner?". */
function bandRank(level: string): number {
  const i = (CEFR_BANDS as string[]).indexOf(level);
  return i === -1 ? CEFR_BANDS.length : i;
}

export function CompetenceMapScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const data = useCompetenceMapStore(selectCompetenceMapData);
  const loading = useCompetenceMapStore(selectCompetenceMapLoading);
  const errorInfo = useCompetenceMapStore(selectCompetenceMapErrorInfo);
  const loadCompetenceMap = useCompetenceMapStore(selectLoadCompetenceMap);
  const clearError = useCompetenceMapStore(selectClearCompetenceMapError);

  const {
    startFocusedSession,
    startingSlug,
    consentModalVisible,
    acceptConsent,
    declineConsent,
  } = useCompetenceFocusSession();

  const [selected, setSelected] = useState<CompetenceMapItem | null>(null);
  const [expandedBands, setExpandedBands] = useState<Record<string, boolean>>({});

  // Load on focus — standard refetch-on-view pattern (no local persistence).
  useFocusEffect(
    useCallback(() => {
      void loadCompetenceMap();
    }, [loadCompetenceMap]),
  );

  const handleRetry = useCallback(() => {
    clearError();
    void loadCompetenceMap();
  }, [clearError, loadCompetenceMap]);

  const handleChipPress = useCallback((competence: CompetenceMapItem) => {
    setSelected(competence);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelected(null);
  }, []);

  const handleToggleBand = useCallback((band: CefrBand) => {
    setExpandedBands((prev) => ({ ...prev, [band]: !prev[band] }));
  }, []);

  // The sheet stays MOUNTED for the whole round-trip, then closes.
  //
  // Closing first (the first version) unmounted the only thing carrying the
  // spinner, so a slow session-creation showed the learner nothing at all —
  // and left the map interactive underneath: tap a second competence during
  // the flight and its sheet opened with an enabled button, whose press was
  // swallowed by the hook's in-flight guard while this handler still closed
  // the sheet. The learner landed in a session about the FIRST competence
  // having just asked for the second, with nothing on screen to say so.
  const handlePractice = useCallback(async () => {
    if (!selected || startingSlug !== null) return;
    await startFocusedSession(selected.slug);
    setSelected(null);
  }, [selected, startingSlug, startFocusedSession]);

  // Group by CEFR band, preserving the server's per-band ordering.
  //
  // Level-less competences get their OWN section rather than riding the
  // learner's band. Filing them under `assessedLevel` was the first attempt
  // and it was wrong three ways: it inflated every learner's own band count,
  // it made the same chip silently move to a different heading when the
  // learner was reassessed, and it put content in the C2 band for a C2
  // learner — quietly breaking CM-021's "C2 renders as an explicit empty
  // state". Anything whose level is null or off-scale lands here, so an
  // unexpected value degrades to a visible chip in a plainly-labelled
  // section instead of vanishing from the map while still counting in the
  // denominator.
  const bands = useMemo(() => {
    if (!data) return [];
    return CEFR_BANDS.map((band) => ({
      band,
      competences: data.competences.filter((c) => c.cefrLevel === band),
    }));
  }, [data]);

  const anyLevel = useMemo(
    () =>
      data
        ? data.competences.filter(
            (c) => c.cefrLevel === null || !(CEFR_BANDS as string[]).includes(c.cefrLevel),
          )
        : [],
    [data],
  );

  // Most overdue first — `dueAt` is on the wire precisely so the client can do
  // this (CMAP-009). The server returns inventory order, so without the sort
  // the section reads alphabetically-by-category and a competence 10 days
  // overdue can sit below one due this morning.
  const dueCompetences = useMemo(
    () =>
      data
        ? data.competences
            .filter((c) => c.isDue)
            .slice()
            .sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? ''))
        : [],
    [data],
  );

  const summaryLine = data ? formatSummaryLine(t, data.summary) : '';
  const attainable = data ? attainableTotal(data.summary) : 0;
  const mastered = data ? data.summary.mastered : 0;
  const progressPct = attainable > 0 ? Math.min(100, (mastered / attainable) * 100) : 0;
  const learnerRank = data ? bandRank(data.assessedLevel) : 0;

  return (
    <View testID="competence-map-screen" style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('competenceMap.title')} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        {!data && loading && (
          <LoadingState testID="competence-map-loading" message={t('competenceMap.loading')} />
        )}

        {!data && !loading && errorInfo && (
          <ErrorState
            testID="competence-map-error"
            message={errorMessage(errorInfo) || t('competenceMap.loadError')}
            retryLabel={t('common.retry')}
            onRetry={handleRetry}
          />
        )}

        {data && (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            {/* ── Header summary ── */}
            <View
              testID="competence-map-summary"
              style={[styles.summaryCard, { backgroundColor: theme.card, borderColor: theme.border }]}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}
              >
                {summaryLine}
              </Text>
              <View style={styles.progressRow}>
                <View style={[styles.progressTrack, { backgroundColor: theme.border }]}>
                  <View
                    style={[
                      styles.progressFill,
                      { backgroundColor: theme.success, width: `${progressPct}%` },
                    ]}
                  />
                </View>
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginLeft: 8, ...ffs }}
                >
                  {t('competenceMap.progressLabel', { mastered, total: attainable })}
                </Text>
              </View>
            </View>

            {/* ── Due for review ── */}
            {dueCompetences.length > 0 && (
              <View testID="competence-map-due">
                <Text
                  allowFontScaling={false}
                  accessibilityRole="header"
                  style={[styles.sectionTitle, { fontSize: FS_MD * fontScale, color: theme.text, ...ffs }]}
                >
                  {t('competenceMap.dueTitle')}
                </Text>
                <View style={styles.grid}>
                  {dueCompetences.map((competence) => (
                    <CompetenceChip
                      key={`due-${competence.slug}`}
                      competence={competence}
                      onPress={handleChipPress}
                      testIDPrefix="competence-map-due-chip"
                    />
                  ))}
                </View>
              </View>
            )}

            {/* ── Level bands ── */}
            {bands.map(({ band, competences }) => {
              const aboveLearner = bandRank(band) > learnerRank;
              const expanded = expandedBands[band] ?? !aboveLearner;
              return (
                <View key={band} testID={`competence-map-band-${band}`}>
                  <TouchableOpacity
                    testID={`competence-map-band-toggle-${band}`}
                    accessibilityRole="button"
                    accessibilityState={{ expanded }}
                    // Label carries the COUNT as well as the title: an explicit
                    // label overrides the children, so naming only the band
                    // left a screen-reader user unable to tell whether
                    // expanding "C1 · Avanzado" would yield five chips or none.
                    accessibilityLabel={`${translateBandTitle(t, band)}, ${
                      aboveLearner
                        ? t('competenceMap.bandLater', { count: competences.length })
                        : t('competenceMap.bandCount', { count: competences.length })
                    }`}
                    onPress={() => handleToggleBand(band)}
                    activeOpacity={0.7}
                    style={styles.bandHeader}
                  >
                    <Text
                      allowFontScaling={false}
                      style={[styles.sectionTitle, { fontSize: FS_MD * fontScale, color: theme.text, ...ffs }]}
                    >
                      {translateBandTitle(t, band)}
                    </Text>
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginLeft: 8, ...ffs }}
                    >
                      {aboveLearner
                        ? t('competenceMap.bandLater', { count: competences.length })
                        : t('competenceMap.bandCount', { count: competences.length })}
                    </Text>
                    <View style={styles.bandSpacer} />
                    <Ionicons
                      name={expanded ? 'chevron-up' : 'chevron-down'}
                      size={Math.round(FS_SM * fontScale) + 4}
                      color={theme.textSecondary}
                    />
                  </TouchableOpacity>

                  {competences.length === 0 ? (
                    // CM-021: a level with no competence renders as an explicit
                    // empty state, never as a missing band. C2 is that level
                    // today, by decision rather than oversight.
                    <Text
                      testID={`competence-map-band-empty-${band}`}
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginBottom: 8, ...ffs }}
                    >
                      {t('competenceMap.bandEmpty')}
                    </Text>
                  ) : (
                    expanded && (
                      <View style={styles.grid}>
                        {competences.map((competence) => (
                          <CompetenceChip
                            key={competence.slug}
                            competence={competence}
                            onPress={handleChipPress}
                          />
                        ))}
                      </View>
                    )
                  )}
                </View>
              );
            })}

            {/* ── Level-less competences ── */}
            {anyLevel.length > 0 && (
              <View testID="competence-map-band-any">
                <Text
                  allowFontScaling={false}
                  accessibilityRole="header"
                  style={[styles.sectionTitle, { fontSize: FS_MD * fontScale, color: theme.text, ...ffs }]}
                >
                  {t('competenceMap.bands.any')}
                </Text>
                <View style={styles.grid}>
                  {anyLevel.map((competence) => (
                    <CompetenceChip
                      key={competence.slug}
                      competence={competence}
                      onPress={handleChipPress}
                    />
                  ))}
                </View>
              </View>
            )}
          </ScrollView>
        )}
      </WebContainer>

      {selected && (
        <CompetenceDetailSheet
          competence={selected}
          visible
          onClose={handleCloseDetail}
          onPractice={handlePractice}
          // Any start in flight disables the button, not just this slug's:
          // the hook refuses a concurrent start anyway, so an enabled button
          // would be a lie.
          practiceLoading={startingSlug !== null}
        />
      )}

      <AIConsentModal
        visible={consentModalVisible}
        onAccept={acceptConsent}
        onDecline={declineConsent}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  summaryCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  sectionTitle: {
    fontWeight: '700',
    marginTop: 20,
    marginBottom: 10,
  },
  bandHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bandSpacer: {
    flex: 1,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
});
